/**
 * Offline Data Manager - IndexedDB wrapper for offline support
 * Handles caching API responses and queuing offline changes
 */

const DB_NAME = 'ecoaudit-offline-db';
const DB_VERSION = 1;

// Store names
const STORES = {
  CACHE: 'api-cache',
  SYNC_QUEUE: 'sync-queue',
  SETTINGS: 'settings'
};

let db = null;

/**
 * Initialize IndexedDB
 */
export const initOfflineDB = () => {
  return new Promise((resolve, reject) => {
    if (db) {
      resolve(db);
      return;
    }

    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onerror = () => {
      console.error('Failed to open IndexedDB:', request.error);
      reject(request.error);
    };

    request.onsuccess = () => {
      db = request.result;
      console.log('IndexedDB initialized');
      resolve(db);
    };

    request.onupgradeneeded = (event) => {
      const database = event.target.result;

      // API Cache Store - stores API responses
      if (!database.objectStoreNames.contains(STORES.CACHE)) {
        const cacheStore = database.createObjectStore(STORES.CACHE, { keyPath: 'url' });
        cacheStore.createIndex('timestamp', 'timestamp', { unique: false });
      }

      // Sync Queue Store - stores pending changes
      if (!database.objectStoreNames.contains(STORES.SYNC_QUEUE)) {
        const syncStore = database.createObjectStore(STORES.SYNC_QUEUE, { keyPath: 'id', autoIncrement: true });
        syncStore.createIndex('timestamp', 'timestamp', { unique: false });
        syncStore.createIndex('type', 'type', { unique: false });
      }

      // Settings Store
      if (!database.objectStoreNames.contains(STORES.SETTINGS)) {
        database.createObjectStore(STORES.SETTINGS, { keyPath: 'key' });
      }
    };
  });
};

/**
 * Cache an API response
 */
export const cacheApiResponse = async (url, data, ttl = 5 * 60 * 1000) => {
  try {
    await initOfflineDB();
    const transaction = db.transaction([STORES.CACHE], 'readwrite');
    const store = transaction.objectStore(STORES.CACHE);
    
    await store.put({
      url,
      data,
      timestamp: Date.now(),
      expiresAt: Date.now() + ttl
    });
    
    return true;
  } catch (error) {
    console.error('Failed to cache API response:', error);
    return false;
  }
};

/**
 * Get cached API response
 */
export const getCachedResponse = async (url) => {
  try {
    await initOfflineDB();
    const transaction = db.transaction([STORES.CACHE], 'readonly');
    const store = transaction.objectStore(STORES.CACHE);
    
    return new Promise((resolve, reject) => {
      const request = store.get(url);
      request.onsuccess = () => {
        const result = request.result;
        if (result && result.expiresAt > Date.now()) {
          resolve(result.data);
        } else {
          resolve(null);
        }
      };
      request.onerror = () => reject(request.error);
    });
  } catch (error) {
    console.error('Failed to get cached response:', error);
    return null;
  }
};

/**
 * Add an operation to the sync queue
 */
export const addToSyncQueue = async (operation) => {
  try {
    await initOfflineDB();
    const transaction = db.transaction([STORES.SYNC_QUEUE], 'readwrite');
    const store = transaction.objectStore(STORES.SYNC_QUEUE);
    
    const queueItem = {
      ...operation,
      timestamp: Date.now(),
      status: 'pending'
    };
    
    return new Promise((resolve, reject) => {
      const request = store.add(queueItem);
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  } catch (error) {
    console.error('Failed to add to sync queue:', error);
    throw error;
  }
};

/**
 * Get all pending operations from sync queue
 */
export const getPendingSyncOperations = async () => {
  try {
    await initOfflineDB();
    const transaction = db.transaction([STORES.SYNC_QUEUE], 'readonly');
    const store = transaction.objectStore(STORES.SYNC_QUEUE);
    
    return new Promise((resolve, reject) => {
      const request = store.getAll();
      request.onsuccess = () => {
        const results = request.result.filter(item => item.status === 'pending');
        resolve(results);
      };
      request.onerror = () => reject(request.error);
    });
  } catch (error) {
    console.error('Failed to get pending operations:', error);
    return [];
  }
};

/**
 * Remove an operation from sync queue
 */
export const removeFromSyncQueue = async (id) => {
  try {
    await initOfflineDB();
    const transaction = db.transaction([STORES.SYNC_QUEUE], 'readwrite');
    const store = transaction.objectStore(STORES.SYNC_QUEUE);
    
    return new Promise((resolve, reject) => {
      const request = store.delete(id);
      request.onsuccess = () => resolve(true);
      request.onerror = () => reject(request.error);
    });
  } catch (error) {
    console.error('Failed to remove from sync queue:', error);
    return false;
  }
};

/**
 * Clear expired cache entries
 */
export const clearExpiredCache = async () => {
  try {
    await initOfflineDB();
    const transaction = db.transaction([STORES.CACHE], 'readwrite');
    const store = transaction.objectStore(STORES.CACHE);
    const index = store.index('timestamp');
    
    return new Promise((resolve) => {
      const request = index.openCursor();
      let deleted = 0;
      
      request.onsuccess = (event) => {
        const cursor = event.target.result;
        if (cursor) {
          if (cursor.value.expiresAt < Date.now()) {
            cursor.delete();
            deleted++;
          }
          cursor.continue();
        } else {
          console.log(`Cleared ${deleted} expired cache entries`);
          resolve(deleted);
        }
      };
    });
  } catch (error) {
    console.error('Failed to clear expired cache:', error);
    return 0;
  }
};

/**
 * Get sync queue count
 */
export const getSyncQueueCount = async () => {
  try {
    const pending = await getPendingSyncOperations();
    return pending.length;
  } catch (error) {
    return 0;
  }
};

/**
 * Check if we're online
 */
export const isOnline = () => navigator.onLine;

/**
 * Clear all IndexedDB data (for logout)
 */
export const clearAllCache = async () => {
  try {
    await initOfflineDB();
    
    // Clear all stores
    const transaction = db.transaction([STORES.CACHE, STORES.SYNC_QUEUE, STORES.SETTINGS], 'readwrite');
    
    await Promise.all([
      new Promise((resolve, reject) => {
        const req = transaction.objectStore(STORES.CACHE).clear();
        req.onsuccess = () => resolve();
        req.onerror = () => reject(req.error);
      }),
      new Promise((resolve, reject) => {
        const req = transaction.objectStore(STORES.SYNC_QUEUE).clear();
        req.onsuccess = () => resolve();
        req.onerror = () => reject(req.error);
      }),
      new Promise((resolve, reject) => {
        const req = transaction.objectStore(STORES.SETTINGS).clear();
        req.onsuccess = () => resolve();
        req.onerror = () => reject(req.error);
      })
    ]);
    
    console.log('IndexedDB cache cleared');
    return true;
  } catch (error) {
    console.error('Failed to clear IndexedDB:', error);
    return false;
  }
};

/**
 * Export for debugging
 */
export const getDB = () => db;
